import * as React from 'react';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import { InputFormProps } from '@app/props';

interface InputState<TInput> {
	input: TInput;
	hasChanges: boolean;
	showValidation: boolean;
	inputRef: React.RefObject<any> | undefined;
	queuedInputToFocus: boolean;
}

interface UseInputFormParameters<TInput> {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	initialInput: TInput;
	showValidation?: boolean;
	validators?: ((input: TInput, required: boolean) => ValidatorResponse)[]; 
}

interface UseInputFormResult<TInput = {}, TProps extends InputFormProps = InputFormProps> {
	changeShowValidationFlag: (showValidation: boolean) => void;
	value: TInput;
	label: string;
	isDisabled: boolean;
	doesInputHaveChanges: boolean;
	isInputValid: boolean;
	setInput: (input: TInput) => void;
	focusToInput: () => void;
	applyProps: () => TProps;
}

const useInputForm = <TInput>(params: UseInputFormParameters<TInput>): UseInputFormResult<TInput> => {

	// #region exported methods

	/**
	 * Updates the input value in the state.
	 * @param input
	 */
	const setInput = (input: TInput): void => {
		setInputState((oldInputState) => {
			return {
				...oldInputState,
				input,
				hasChanges: true,
			}
		});
	};

	/**
	 * Focuses to element. 
	 **/
	const focusToInput = (): void => {
		setInputState((oldInputState) => {
			return {
				...oldInputState,
				queuedInputToFocus: true,
			}
		});
	};

	/**
	 * Sets the show validation flag in the state.
	 * @param showValidation
	 */
	const changeShowValidationFlag = (showValidation: boolean): void => {
		setInputState((oldInputState) => {
			return {
				...oldInputState,
				showValidation,
			}
		});
	}

	/**
	 * Returns props for form input. 
	 **/
	const applyProps = (): InputFormProps => {
		// Validate input.
		let validationResult: ValidatorResponse = new ValidatorResponse(true, String.empty());
		if (params.validators !== undefined
			&& params.validators.length > 0
			&& !params.disabled) {
			validationResult = (params.validators.map((validator) => validator(inputState.input, params.required)).find((validatorResult) => !validatorResult.isValid)
				?? new ValidatorResponse(true, String.empty()));
		}

		return {
			id: params.id,
			label: params.label,
			hasErrors: inputState.showValidation ? !validationResult.isValid : false,
			helperText: inputState.showValidation ? validationResult.message : undefined,
			isOptional: !params.required,
			disabled: params.disabled,
			inputRef: registerInputRef,
		}
	};

	// #endregion exported methods


	// #region private methods.

	const registerInputRef = (ref: React.RefObject<any>): void => {
		if (inputState.inputRef === undefined
			|| inputState.inputRef.current === null) {
			setInputState((oldInputState) => {
				return {
					...oldInputState,
					inputRef: ref,
				}
			});
		}
	}

	// #endregion private methods.


	// #region variables

	const [inputState, setInputState] = React.useState<InputState<TInput>>({
		input: params.initialInput,
		hasChanges: false,
		showValidation: params.showValidation ?? false,
		inputRef: undefined,
		queuedInputToFocus: false,
	});

	// #endregion variables

	// #region cached variables

	const isDisabled: boolean = React.useMemo(() => params.disabled, [params.disabled]);
	const validators: ((input: TInput, required: boolean) => ValidatorResponse)[] = React.useMemo(() => {
		return params.validators ?? [];
	}, [params.validators]);
	const isInputValid: boolean = React.useMemo(() => {
		let isValid: boolean = true;
		if (validators.length > 0
			&& !isDisabled) {
			isValid = validators.every((validator) => validator(inputState.input, params.required).isValid);
		}
		return isValid;
	}, [
		validators,
		isDisabled,
		inputState.input,
		params.required,
	]);

	// #endregion cached variables

	// #region life-cycle methods.

	// Called when input ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued input focus-to flag and the input reference is ready, then proceed to focus on the input.
		if (inputState.queuedInputToFocus
			&& inputState.inputRef !== undefined
			&& inputState.inputRef.current !== null) {
			// If input is already focused, then do not attempt to do it again.
			if (document.activeElement !== inputState.inputRef.current) {
				inputState.inputRef.current.focus();
			}
			// Reset the queued input to focus.
			setInputState((oldInputState) => {
				return {
					...oldInputState,
					queuedInputToFocus: false,
				}
			});
		}
	}, [inputState.queuedInputToFocus, inputState.inputRef]);

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		return () => {
			// Remove any queued element ids.
			setInputState((oldInputState) => {
				return {
					...oldInputState,
					showValidation: false,
				}
			});
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		changeShowValidationFlag,
		value: inputState.input,
		label: params.label,
		isDisabled,
		doesInputHaveChanges: inputState.hasChanges,
		isInputValid,
		setInput,
		focusToInput,
		applyProps,
	};
}

// Export parameters.
export {
	UseInputFormParameters,
}

// Export results.
export {
	UseInputFormResult,
}

export default useInputForm;